// Helpers to constrain order pickup date/time selection to a restaurant's
// opening hours, accounting for closed days and a closing-time offset (the
// minimum number of minutes before closing that a pickup is still allowed —
// e.g. customers can't arrive exactly at closing time).
//
// Opening hours are interpreted in the browser's local time, consistent with
// the open/closed logic on the public restaurant page. A note on the checkout
// form already warns customers when the restaurant is in a different timezone.

const MINUTES_IN_DAY = 24 * 60;

// JS Date.getDay(): 0 = Sunday … 6 = Saturday. The schedule entries use full
// English day names (matching utils/schedule-helper-variables weekdaysByName).
const SCHEDULE_DAY_BY_JS_DAY = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday'
};

const timeStringToMinutes = (timeString) => {
  if (typeof timeString !== 'string') return null;

  const [hours, minutes] = timeString.split(':').map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  return (hours * 60) + minutes;
};

const isSameCalendarDay = (dateA, dateB) => dateA.getFullYear() === dateB.getFullYear() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getDate() === dateB.getDate();

const findDayEntry = (schedule, dayKey) => (Array.isArray(schedule) ?
  schedule.find(entry => typeof entry?.day === 'string' && entry.day.toLowerCase() === dayKey) :
  null);

const dayHasClosure = (closedDays, date) => (Array.isArray(closedDays) ?
  closedDays.some(closedDay => closedDay?.date instanceof Date && isSameCalendarDay(closedDay.date, date)) :
  false);

// Returns true when the restaurant has at least one configured time entry on any
// weekday. When false, no opening-hours constraint is applied (the restaurant has
// not set up a schedule yet, so we must not lock customers out of ordering).
const scheduleHasHours = schedule => Array.isArray(schedule) && schedule.some(entry => (
  Array.isArray(entry?.timeEntries) && entry.timeEntries.some(timeEntry => (
    timeStringToMinutes(timeEntry?.openTime) !== null &&
    timeStringToMinutes(timeEntry?.closeTime) !== null
  ))
));

// Builds the list of allowed minute ranges ({ startMinute, endMinute }, where
// endMinute is exclusive) within the given calendar date, applying the closing
// offset and accounting for overnight entries that spill in from the previous day.
const buildAvailableIntervalsForDate = (schedule, closedDays, offsetMinutes, date) => {
  const intervals = [];
  const safeOffset = Number.isFinite(offsetMinutes) && offsetMinutes > 0 ? offsetMinutes : 0;

  // Same-day opening hours.
  if (!dayHasClosure(closedDays, date)) {
    const dayEntry = findDayEntry(schedule, SCHEDULE_DAY_BY_JS_DAY[date.getDay()]);

    (dayEntry?.timeEntries || []).forEach((timeEntry) => {
      const openMinute = timeStringToMinutes(timeEntry?.openTime);
      const closeMinute = timeStringToMinutes(timeEntry?.closeTime);

      if (openMinute === null || closeMinute === null) return;

      const isOvernight = closeMinute <= openMinute;
      const effectiveClose = (isOvernight ? closeMinute + MINUTES_IN_DAY : closeMinute) - safeOffset;

      if (effectiveClose <= openMinute) return;

      intervals.push({
        endMinute: Math.min(effectiveClose, MINUTES_IN_DAY),
        startMinute: openMinute
      });
    });
  }

  // Overnight hours spilling in from the previous day's entries — only when the
  // target date itself is open (a closed day is fully unavailable, even in the
  // early-morning hours carried over from the previous day).
  const previousDate = new Date(date);

  previousDate.setDate(previousDate.getDate() - 1);

  if (!dayHasClosure(closedDays, date) && !dayHasClosure(closedDays, previousDate)) {
    const previousDayEntry = findDayEntry(schedule, SCHEDULE_DAY_BY_JS_DAY[previousDate.getDay()]);

    (previousDayEntry?.timeEntries || []).forEach((timeEntry) => {
      const openMinute = timeStringToMinutes(timeEntry?.openTime);
      const closeMinute = timeStringToMinutes(timeEntry?.closeTime);

      if (openMinute === null || closeMinute === null) return;

      if (closeMinute > openMinute) return; // not overnight, no spill

      const spillEnd = closeMinute - safeOffset;

      if (spillEnd > 0) {
        intervals.push({
          endMinute: spillEnd,
          startMinute: 0
        });
      }
    });
  }

  return intervals;
};

/**
 * Builds availability predicates for the order pickup date/time picker.
 *
 * @param {Object} params
 * @param {Array} params.schedule       Normalized weekly schedule (day + timeEntries).
 * @param {Array} params.closedDays     Closed days ({ date: Date }).
 * @param {number} params.offsetMinutes Minutes before closing to stop accepting pickups.
 * @returns {{ isConstrained: boolean, isDateAvailable: (date: Date) => boolean, isTimeAvailable: (dateTime: Date) => boolean }}
 */
const buildOrderAvailability = ({
  closedDays = [],
  offsetMinutes = 0,
  schedule = []
} = {}) => {
  const isConstrained = scheduleHasHours(schedule);

  if (!isConstrained) {
    return {
      isConstrained: false,
      isDateAvailable: () => true,
      isTimeAvailable: () => true
    };
  }

  const isDateAvailable = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;

    return buildAvailableIntervalsForDate(schedule, closedDays, offsetMinutes, date).length > 0;
  };

  const isTimeAvailable = (dateTime) => {
    if (!(dateTime instanceof Date) || Number.isNaN(dateTime.getTime())) return false;

    const intervals = buildAvailableIntervalsForDate(schedule, closedDays, offsetMinutes, dateTime);
    const minuteOfDay = (dateTime.getHours() * 60) + dateTime.getMinutes();

    return intervals.some(interval => minuteOfDay >= interval.startMinute && minuteOfDay < interval.endMinute);
  };

  return {
    isConstrained: true,
    isDateAvailable,
    isTimeAvailable
  };
};

export default buildOrderAvailability;
